/**
 * headFollowRuntime — head/neck camera-tracking.
 *
 * The face-graph `headFollow` node writes the sentinel `__headfollow__` into
 * the evaluator output (mirrors `__livelink__`). Per-node config is stashed
 * here via setConfig() during graph evaluation; AvatarModel calls
 * applyHeadFollow() once per frame AFTER mixer.update() so we can slerp from
 * the clip-driven pose toward the look-at target without racing the mixer.
 *
 * The look-at is a world-space **explicit-up aim** (equivalent to Unreal's
 * Aim Constraint with an Up axis): the head's target world orientation is built
 * from `clampedDir` as forward and world +Y as up. A share of the correction
 * is distributed across neck_01 (30%) / neck_02 (40%) / head (30%) so the
 * skinning deforms smoothly across the neck rather than stretching at a single
 * joint. After the chain distribution, a final pass enforces the head bone's
 * absolute local quaternion as `parentWorld⁻¹ · qTarget`, blended from the clip
 * pose by the smoothed weight — this guarantees the head's world orientation
 * lands at qTarget regardless of any `_headWorld` staleness in the chain math.
 *
 * At sentinel weight = 0 the bones stay at their clip rotation (we slerp by 0).
 * At weight = 1 they fully adopt the look-at target. Weight in between
 * crossfades. `smoothing` drives a framerate-independent exponential follow on
 * both yaw/pitch and weight, so variable toggles fade cleanly instead of
 * snapping.
 */

import * as THREE from 'three'
import { debugState } from './debugState.js'
import { clampYawToBody } from './headAim.js'

const BONE_NAMES  = ['neck_01', 'neck_02', 'head']
// Chain shares spread a *little* of the aim rotation into the neck so the
// skinning deforms smoothly across the upper-neck → head boundary. Shares 0/0
// gave a "giraffe neck" (head bone took the full rotation locally → skin
// stretched). Shares 0.30/0.40 worked for skinning but rotated the neck enough
// to translate the head's *position* visibly on clip-wrap frames where
// `qFullWorld` spikes. Small shares (10%/15%) keep the skinning smooth while
// limiting how far the neck can swing the head's position. The head's share
// is 0 because the final-pass enforcement below writes it absolutely.
const BONE_SHARES = { neck_01: 0.10, neck_02: 0.15, head: 0.0 }

const DEFAULT_CONFIG = { maxYaw: 45, maxPitch: 25, smoothing: 0.15, deadbandDeg: 2 }

// Scratch objects reused each frame to avoid GC churn on the render hot path.
const _camPos       = new THREE.Vector3()
const _pivotPos     = new THREE.Vector3()
const _toCam        = new THREE.Vector3()
const _clampedDir   = new THREE.Vector3()
const _right        = new THREE.Vector3()
const _newUp        = new THREE.Vector3()
const _negFwd       = new THREE.Vector3()
const _targetMat    = new THREE.Matrix4()
const _targetQuat   = new THREE.Quaternion()
const _headWorld    = new THREE.Quaternion()
const _headWorldInv = new THREE.Quaternion()
const _qFullWorld   = new THREE.Quaternion()
const _qShare       = new THREE.Quaternion()
const _parentWorld  = new THREE.Quaternion()
const _parentWorldI = new THREE.Quaternion()
const _qLocalCorr   = new THREE.Quaternion()
const _Q_IDENTITY   = new THREE.Quaternion()
// Smoothed version of _qFullWorld for the chain distribution. Damps clip-wrap
// spikes (when the body clip loops, _headWorld jumps in one frame, making
// _qFullWorld spike; chain rotation amplifies the spike into visible head/neck
// displacement). The smoothed value passes normal motion through unchanged but
// absorbs single-frame spikes over a few frames.
const _qFullWorldChain = new THREE.Quaternion()
const _WORLD_UP     = new THREE.Vector3(0, 1, 0)
// Scratch for reading the avatar body's current world yaw (ENG-609): the head
// look-at clamps relative to this, not world +Z, so locomotion-driven body
// rotation doesn't let the head over-twist.
const _bodyQuat     = new THREE.Quaternion()
const _bodyEuler    = new THREE.Euler()
// Scratch for the final-pass head enforcement (independent of _headWorld).
const _headParentWorld    = new THREE.Quaternion()
const _headParentWorldI   = new THREE.Quaternion()
const _headTargetLocal    = new THREE.Quaternion()

// Reference frame in WORLD space. This rig's avatar faces world +Z at rest (the
// default camera sits on +Z and sees the face) with world up = +Y, and no
// rotation is applied to the loaded GLB. We aim in world space (minimal-arc)
// rather than around hard-coded bone-local axes, so the look-at is roll-free and
// self-calibrating regardless of how the clip posed the spine. DEPENDENCY: if an
// avatar is ever loaded with a root rotation, this +Z forward reference no longer
// holds and would need to be derived from the rig.
const _REST_FORWARD = new THREE.Vector3(0, 0, 1)
// Bone-local axis convention for the MetaHuman neck/head chain:
//   local +X = up the spine (character up)
//   local -Y = forward (face direction)
//   local +Z = "left" (character's left)
// The target orientation builder (makeBasis call below) maps these onto world
// axes derived from clampedDir + world up.

// ── Diagnostic ring buffer (opt-in) ───────────────────────────────────────────
// Opt-in via `window.__HEAD_FOLLOW_LOG__ = true`. When the flag is off the
// recording branch is skipped entirely → zero per-frame cost. When on, each
// frame writes one row into a preallocated ring; `headFollowRuntime.dumpLog()`
// returns the chronological CSV and copies it to the clipboard.
const _LOG_CAPACITY = 600   // ≈10s @60fps; cheap (a few hundred plain objects)
const _LOG_COLUMNS  = [
  't','rawYaw','rawPitch','smYaw','smPitch','smWeight','weight',
  'pivotX','pivotY','pivotZ','headLeanDeg','targetX','targetY','targetZ','clamped',
  // Actual head-facing direction in world AFTER our write — to detect 1-frame
  // glances where the visible head diverges from the intended target dir.
  'headFwdX','headFwdY','headFwdZ',
  // Root-cause fields:
  //   preQ*       — head's world quaternion BEFORE the per-bone loop runs
  //                 (= _headWorld). Used with target to compute correction.
  //   corrAngleDeg — angle (deg) of the rotation qFullWorld = qTarget · _headWorldInv.
  //                 Near 0 means "correction is identity → no aim applied".
  //   postQ*      — head's LOCAL quaternion AFTER our write. Compare to preQ for
  //                 evidence of what we actually committed to the bone.
  'preQx','preQy','preQz','preQw','corrAngleDeg','postQx','postQy','postQz','postQw',
]
function _allocLog() {
  const buf = new Array(_LOG_CAPACITY)
  for (let i = 0; i < _LOG_CAPACITY; i++) {
    const r = {}
    for (const k of _LOG_COLUMNS) r[k] = 0
    buf[i] = r
  }
  return { buf, write: 0, count: 0 }
}

export const headFollowRuntime = {
  config: { ...DEFAULT_CONFIG },
  // Smoothed look-at target shared across the chain.
  _smoothed: { yaw: 0, pitch: 0, weight: 0 },
  _bones: null,
  _sceneRoot: null,
  // Per-bone cache of the mixer-written clip quaternion + what we last wrote
  // back. Needed to avoid a runaway feedback loop when the active clip has
  // no track for a bone — in that case the mixer leaves bone.quaternion
  // alone, which means our previous-frame override is still there. Without
  // this cache, each frame's premultiply would compound and the head spins.
  _clipCache: null,
  // Lazy-initialised on first record. Null when never enabled this session.
  _log: null,
  // Frames-remaining counter for the post-clip-loop chain freeze. Set by
  // notifyClipLoop() (called from AvatarModel on mixer 'loop' events). While
  // non-zero, applyHeadFollow skips the per-frame slerp that updates
  // `_qFullWorldChain` — the chain rotation magnitude is FROZEN at its
  // pre-wrap value, so the discrete `_qFullWorld` spike from the clip
  // discontinuity never enters the chain. Chain shares stay normal, so the
  // neck continues to participate naturally with its existing rotation — no
  // share-to-zero toggle artifact. Three frames covers the wrap + LBB's
  // mixer.update(0) re-evaluations.
  _skipChainFrames: 0,
  notifyClipLoop() { this._skipChainFrames = 3 },

  /**
   * Dump the diagnostic ring buffer as CSV (chronological order) and copy it
   * to the clipboard. Returns the CSV string.
   *
   * Usage:
   *   window.__HEAD_FOLLOW_LOG__ = true   // start recording
   *   …reproduce the issue…
   *   window.__HEAD_FOLLOW__.dumpLog()    // paste into a spreadsheet / Slack
   */
  dumpLog() {
    const log = this._log
    if (!log || log.count === 0) {
      console.warn('[head-follow] no log data — set window.__HEAD_FOLLOW_LOG__ = true, reproduce, then call dumpLog() again')
      return ''
    }
    const lines = [_LOG_COLUMNS.join(',')]
    const N = log.count
    const start = log.count < _LOG_CAPACITY ? 0 : log.write   // oldest entry index
    for (let i = 0; i < N; i++) {
      const r = log.buf[(start + i) % _LOG_CAPACITY]
      lines.push(_LOG_COLUMNS.map(c => r[c]).join(','))
    }
    const csv = lines.join('\n')
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(csv).then(
        () => console.log(`[head-follow] dumpLog: ${N} rows copied to clipboard`),
        (err) => console.warn('[head-follow] clipboard write failed:', err),
      )
    } else {
      console.log(`[head-follow] dumpLog: ${N} rows (clipboard unavailable)`)
    }
    return csv
  },

  setConfig(data) {
    if (!data) return
    const c = this.config
    c.maxYaw      = Number(data.maxYaw      ?? DEFAULT_CONFIG.maxYaw)
    c.maxPitch    = Number(data.maxPitch    ?? DEFAULT_CONFIG.maxPitch)
    c.smoothing   = Number(data.smoothing   ?? DEFAULT_CONFIG.smoothing)
    c.deadbandDeg = Number(data.deadbandDeg ?? DEFAULT_CONFIG.deadbandDeg)
  },

  _resolveBones(scene) {
    if (this._bones && this._sceneRoot === scene) return this._bones
    if (!scene) return null
    const bones = {}
    const clipCache = {}
    for (const name of BONE_NAMES) {
      const bone = scene.getObjectByName(name)
      if (!bone) return null
      bones[name] = bone
      clipCache[name] = {
        clip:        new THREE.Quaternion().copy(bone.quaternion),   // rest pose fallback
        lastWritten: new THREE.Quaternion(),
        hasWritten:  false,
      }
    }
    this._bones      = bones
    this._sceneRoot  = scene
    this._clipCache  = clipCache
    return bones
  },
}

if (typeof window !== 'undefined') {
  window.__HEAD_FOLLOW__ = headFollowRuntime
}

/**
 * applyHeadFollow — post-mixer bone rotation.
 *
 * @param {THREE.Object3D} scene   avatar scene root (groupRef.current)
 * @param {THREE.Camera}   camera  R3F default camera
 * @param {number}         weight  __headfollow__ sentinel weight from face graph
 * @param {number}         delta   seconds since last frame
 */
export function applyHeadFollow(scene, camera, weight, delta) {
  const bones = headFollowRuntime._resolveBones(scene)
  if (!bones || !camera) return

  const cfg = headFollowRuntime.config
  // Framerate-independent exponential follow: higher smoothing = snappier.
  const lerp = 1 - Math.exp(-cfg.smoothing * (delta || 1 / 60) * 60)

  // ── Direction to camera + interpretable yaw/pitch (also the debug readout) ──
  // Use neck_02 as the look-at pivot (mid of the chain). Decompose the world
  // direction-to-camera off the known world rest-forward (-Z) / up (+Y), so the
  // angles are sign-correct and ~0 when the camera is dead-ahead.
  const pivot = bones.neck_02
  pivot.getWorldPosition(_pivotPos)
  camera.getWorldPosition(_camPos)
  _toCam.subVectors(_camPos, _pivotPos)
  if (_toCam.lengthSq() < 1e-8) return   // camera ~on pivot: skip aim this frame
  _toCam.normalize()

  //   yaw  = rotation about world +Y (0 when facing +Z); + turns toward +X
  //   pitch = rotation about world right; + looks up
  const rawYaw   = Math.atan2(_toCam.x, _toCam.z)
  const rawPitch = Math.asin(THREE.MathUtils.clamp(_toCam.y, -1, 1))

  const maxYaw   = cfg.maxYaw   * Math.PI / 180
  const maxPitch = cfg.maxPitch * Math.PI / 180
  // Clamp YAW relative to the BODY's current forward, not world +Z (ENG-609).
  // The avatar group is yawed by locomotion (movementComponent sets
  // group.rotation.y to face the walk direction); clamping around a fixed world
  // axis let the head twist body-yaw + maxYaw off the torso ("exorcist"). bodyYaw
  // is the group's world Y rotation — at rest it's 0 (avatar faces +Z), so idle
  // behaviour is unchanged. tgtYaw is now RELATIVE to the body forward.
  scene.getWorldQuaternion(_bodyQuat)
  _bodyEuler.setFromQuaternion(_bodyQuat, 'YXZ')
  const bodyYaw  = _bodyEuler.y
  const tgtYaw   = clampYawToBody(rawYaw, bodyYaw, maxYaw)
  const tgtPitch = THREE.MathUtils.clamp(rawPitch, -maxPitch, maxPitch)

  // ── Smooth target + weight so toggles fade instead of snapping ─────────
  // Deadband: if neither yaw nor pitch has moved beyond the configured threshold
  // from the currently-smoothed state, hold position. Once EITHER axis exceeds
  // the threshold, smooth both axes together (avoids weird single-axis snapping).
  // Gives a small natural delay before the head starts tracking + ignores
  // micro-jitter from breathing-driven pivot wobble.
  const s = headFollowRuntime._smoothed
  const deadband = cfg.deadbandDeg * Math.PI / 180
  if (Math.abs(tgtYaw - s.yaw) > deadband || Math.abs(tgtPitch - s.pitch) > deadband) {
    s.yaw    += (tgtYaw   - s.yaw)   * lerp
    s.pitch  += (tgtPitch - s.pitch) * lerp
  }
  // Weight always smooths regardless of deadband — toggling HF on/off should
  // fade reliably, never get stuck below the threshold.
  s.weight += (weight - s.weight) * lerp

  // Rebuild a clean clamped look direction from the SMOOTHED angles. s.yaw is
  // RELATIVE to the body forward (ENG-609), so compose bodyYaw back to get the
  // world-space look yaw — the head is kept within ±maxYaw of wherever the torso
  // points. At bodyYaw=0 this is identical to the old world-anchored behaviour.
  const worldYaw = bodyYaw + s.yaw
  const cosP = Math.cos(s.pitch)
  _clampedDir.set(
    Math.sin(worldYaw) * cosP,
    Math.sin(s.pitch),
    Math.cos(worldYaw) * cosP,
  )

  // ── Build an explicit-up aim correction (mirrors UE's Aim Constraint w/ Up) ──
  // Construct the head's *absolute* target world orientation from clampedDir as
  // forward and world +Y as the up reference. Then the correction is the rotation
  // that takes the head's current world orientation to that target. This is the
  // critical difference from a minimal-arc delta: an explicit-up aim *replaces*
  // the chain's free orientation, so any roll the body clip injected into the
  // spine (and inherited by the head) is removed — exactly what UE's Aim
  // Constraint does. Bone-local axis convention (MetaHuman): +X = up, -Y =
  // forward, +Z = "left" (character left). Build the rotation matrix whose
  // columns are those local axes expressed in world space.
  _right.crossVectors(_clampedDir, _WORLD_UP).normalize()   // local +Z in world (character left)
  _newUp.crossVectors(_right, _clampedDir).normalize()      // local +X in world (head up)
  _negFwd.copy(_clampedDir).negate()                        // local +Y in world (behind)
  _targetMat.makeBasis(_newUp, _negFwd, _right)
  _targetQuat.setFromRotationMatrix(_targetMat)

  const head = bones.head
  head.getWorldQuaternion(_headWorld)
  _headWorldInv.copy(_headWorld).invert()
  _qFullWorld.multiplyQuaternions(_targetQuat, _headWorldInv)
  // Damp _qFullWorld for the chain distribution to absorb normal motion.
  // The head's *aim* orientation is unaffected — it's enforced absolutely by
  // the final pass below using qTarget directly. This only smooths how much
  // the neck participates frame-to-frame.
  //
  // On the few frames immediately following a clip-loop event (mixer 'loop'),
  // skip the slerp entirely: `_qFullWorld` would be spiked by the discrete
  // clip-end → clip-start jump in the spine, and we don't want that spike to
  // contaminate the smoothed chain value. Holding `_qFullWorldChain` at its
  // pre-wrap value keeps the neck's chain rotation stable across the wrap.
  if (headFollowRuntime._skipChainFrames > 0) {
    headFollowRuntime._skipChainFrames--
  } else {
    _qFullWorldChain.slerp(_qFullWorld, lerp)
  }

  // ── Distribute a share of the correction across the chain ──────────────
  // For each bone we apply `share*weight` of the SAME world rotation, expressed
  // in the bone's parent-local frame (conjugation by the parent's world quat),
  // premultiplied onto the cached clip pose. Bones are processed parent→child
  // and each write refreshes matrixWorld, so the child reads the updated parent
  // and the shares compose toward the full aim.
  //
  // Clip-cache: refresh `cc.clip` only when the mixer (or layered-bone-blend)
  // actually wrote a new value this frame (detected by comparing against what we
  // wrote last frame). If nothing touched the bone, reuse the cached clip pose —
  // preventing a runaway feedback where the override compounds and the head spins.
  const cache = headFollowRuntime._clipCache
  for (const name of BONE_NAMES) {
    const bone = bones[name]
    const cc   = cache[name]

    const mixerWrote = !cc.hasWritten || !bone.quaternion.equals(cc.lastWritten)
    if (mixerWrote) cc.clip.copy(bone.quaternion)

    const share = BONE_SHARES[name]
    _qShare.copy(_Q_IDENTITY).slerp(_qFullWorldChain, share * s.weight)

    const parent = bone.parent
    if (parent) {
      parent.getWorldQuaternion(_parentWorld)
      _parentWorldI.copy(_parentWorld).invert()
      // qLocalCorr = parentWorld⁻¹ · qShare · parentWorld
      _qLocalCorr.multiplyQuaternions(_parentWorldI, _qShare).multiply(_parentWorld)
    } else {
      _qLocalCorr.copy(_qShare)
    }

    bone.quaternion.multiplyQuaternions(_qLocalCorr, cc.clip)
    cc.lastWritten.copy(bone.quaternion)
    cc.hasWritten = true

    // Refresh world matrix so the child bone's getWorldQuaternion sees this write.
    bone.updateWorldMatrix(true, false)
  }

  // ── Final pass: enforce the head's absolute target orientation ──────────────
  // The chain share-distribution above is sensitive to `_headWorld` staleness.
  // Specifically: if `head.getWorldQuaternion()` returns a stale matrixWorld
  // (last frame's already-corrected pose), `_qFullWorld` collapses to identity
  // and no correction is applied — the head ends at the freshly refreshed clip
  // pose. This was the source of the ~once-per-clip-cycle "glance away" glitch.
  //
  // Bypass that fragility by directly enforcing the head's target world
  // orientation as the FINAL write. parent's world here is taken AFTER the
  // share-distribution loop has updated neck_01 and neck_02, so it's fresh.
  // The neck-bone share contributions still happen above for the organic
  // spine-up motion; this pass just guarantees the head itself lands at qTarget
  // (scaled by smoothed weight so toggles still fade).
  const headBone   = bones.head
  const headParent = headBone.parent
  const headCache  = cache.head
  if (headParent) {
    headParent.getWorldQuaternion(_headParentWorld)
    _headParentWorldI.copy(_headParentWorld).invert()
    // head_local_target = parentWorld⁻¹ · qTarget   (so headWorld = qTarget)
    _headTargetLocal.multiplyQuaternions(_headParentWorldI, _targetQuat)
  } else {
    _headTargetLocal.copy(_targetQuat)
  }
  // Blend from clip pose to target by smoothed weight. At weight=0, stays at
  // clip; at weight=1, head's world becomes exactly qTarget.
  headBone.quaternion.copy(headCache.clip).slerp(_headTargetLocal, s.weight)
  headCache.lastWritten.copy(headBone.quaternion)
  headCache.hasWritten = true
  headBone.updateWorldMatrix(true, false)

  debugState.headFollow = {
    weight: s.weight,
    yaw:    s.yaw   * 180 / Math.PI,
    pitch:  s.pitch * 180 / Math.PI,
  }

  // ── Diagnostic recording (opt-in, see top-of-file ring buffer block) ────
  // Cheap path-out when the flag is off — single property read on window.
  if (typeof window !== 'undefined' && window.__HEAD_FOLLOW_LOG__) {
    const log = headFollowRuntime._log || (headFollowRuntime._log = _allocLog())
    const rec = log.buf[log.write]
    rec.t        = (typeof performance !== 'undefined' ? performance.now() : Date.now())
    rec.rawYaw   = rawYaw   * 180 / Math.PI
    rec.rawPitch = rawPitch * 180 / Math.PI
    rec.smYaw    = s.yaw    * 180 / Math.PI
    rec.smPitch  = s.pitch  * 180 / Math.PI
    rec.smWeight = s.weight
    rec.weight   = weight
    rec.pivotX   = _pivotPos.x;  rec.pivotY = _pivotPos.y;  rec.pivotZ = _pivotPos.z
    // headLeanDeg = vertical component of the head bone's local +Z ("left")
    // axis in world space. A roll-free aim keeps this near 0 regardless of
    // yaw/pitch; same metric the regression test asserts.
    const m = head.matrixWorld.elements
    const zlen = Math.hypot(m[8], m[9], m[10]) || 1
    rec.headLeanDeg = Math.asin(m[9] / zlen) * 180 / Math.PI
    rec.targetX  = _clampedDir.x; rec.targetY = _clampedDir.y; rec.targetZ = _clampedDir.z
    // tgtYaw/tgtPitch === rawYaw/rawPitch (exact) when in range → clamp not hit.
    rec.clamped  = (tgtYaw !== rawYaw || tgtPitch !== rawPitch) ? 1 : 0
    // Head's actual world-facing direction *after* our write this frame.
    // head.matrixWorld was refreshed by updateWorldMatrix in the bone loop above.
    // Local forward is -Y, so the world forward column is -col1 of matrixWorld.
    const mh = head.matrixWorld.elements
    const fxn = -mh[4], fyn = -mh[5], fzn = -mh[6]
    const flen = Math.hypot(fxn, fyn, fzn) || 1
    rec.headFwdX = fxn / flen; rec.headFwdY = fyn / flen; rec.headFwdZ = fzn / flen
    // Pre-loop head world quaternion (captured into _headWorld earlier).
    rec.preQx = _headWorld.x; rec.preQy = _headWorld.y; rec.preQz = _headWorld.z; rec.preQw = _headWorld.w
    // Angle of the correction quaternion qFullWorld = qTarget · _headWorldInv.
    // Quat → angle: 2·acos(|w|).
    const w = Math.min(1, Math.max(-1, Math.abs(_qFullWorld.w)))
    rec.corrAngleDeg = 2 * Math.acos(w) * 180 / Math.PI
    // Head's LOCAL quaternion after our write (what's actually committed).
    rec.postQx = head.quaternion.x; rec.postQy = head.quaternion.y; rec.postQz = head.quaternion.z; rec.postQw = head.quaternion.w
    log.write = (log.write + 1) % _LOG_CAPACITY
    if (log.count < _LOG_CAPACITY) log.count++
  }
}
